import type { KeyboardEvent } from 'react';
import type { Occurrence, OccurrenceWithTimeSpent } from '../../../models/occurrence';
import type { Step } from '../../../models/step';
import {
    getCurrentStep,
    getOccurrenceTotalMs,
    getOccurrenceTypeLabelId,
    getStepLabelId,
    getStepState,
    getStepTiming,
    toStepValue,
} from '../trackingUtils';
import CriticalityBadge from './CriticalityBadge';
import useMtText from './useMtText';
import './TrackingTable.scss';

interface ITrackingTableProps {
    rows: Occurrence[];
    stepKeys: string[];
    steps: Step[];
    timeSpentById: Map<string, OccurrenceWithTimeSpent>;
    loading: boolean;
    selectedId?: string | null;
    onOpen: (occurrence: Occurrence) => void;
    emptyMessage: string;
}

const SKELETON_ROWS = ['s1', 's2', 's3', 's4', 's5', 's6'];
const COLUMN_COUNT = 8;

const TrackingTable = ({
    rows,
    stepKeys,
    steps,
    timeSpentById,
    loading,
    selectedId,
    onOpen,
    emptyMessage,
}: ITrackingTableProps) => {
    const { t, msg, formatDuration, formatDateTime } = useMtText();

    const renderCurrentStep = (occurrence: Occurrence) => {
        const current = getCurrentStep(occurrence);
        if (!current) {
            return <b>{t(occurrence.finished ? 'table.finished' : 'table.notStarted')}</b>;
        }
        const { elapsed, estimate, late } = getStepTiming(occurrence, steps, current);
        const percent = elapsed !== undefined && estimate ? Math.min(100, (elapsed / estimate) * 100) : 0;
        return (
            <>
                <b>
                    {msg(getStepLabelId(current))}
                    {late && <span className='mt-alert'>{t('table.alert')}</span>}
                </b>
                <small className='mt-num'>
                    {estimate
                        ? t('table.elapsedOfEstimate', {
                              elapsed: formatDuration(elapsed),
                              estimate: formatDuration(estimate),
                          })
                        : formatDuration(elapsed)}
                </small>
                {estimate !== undefined && (
                    <span className='mt-bar'>
                        <i className={late ? 'mt-bar--late' : ''} style={{ width: `${percent}%` }} />
                    </span>
                )}
            </>
        );
    };

    const renderProgress = (occurrence: Occurrence) => (
        <div className='mt-prog' style={{ gridTemplateColumns: `repeat(${stepKeys.length}, 1fr)` }}>
            {stepKeys.map(stepKey => {
                const step = toStepValue(stepKey);
                const state = getStepState(occurrence, step);
                const late = state === 'current' && getStepTiming(occurrence, steps, step).late;
                return (
                    <i
                        key={stepKey}
                        className={`mt-prog__${state}${late ? ' mt-prog__late' : ''}`}
                        title={msg(getStepLabelId(step))}
                    />
                );
            })}
        </div>
    );

    const handleKeyDown = (occurrence: Occurrence) => (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === 'Enter' && event.target === event.currentTarget) {
            event.preventDefault();
            onOpen(occurrence);
        }
    };

    return (
        <div className='mt-tablewrap'>
            <table className='mt-table'>
                <thead>
                    <tr>
                        <th>{msg('intl-msg:kanban.tableConfigs.vehicle')}</th>
                        <th>{msg('intl-msg:kanban.filterRow.criticality')}</th>
                        <th>{msg('intl-msg:kanban.filterRow.customer')}</th>
                        <th>{t('table.dealership')}</th>
                        <th>{t('table.currentStep')}</th>
                        <th>{t('table.progress', { count: stepKeys.length })}</th>
                        <th>{t('summary.totalTime')}</th>
                        <th>{t('table.updated')}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && !rows.length
                        ? SKELETON_ROWS.map(key => (
                              <tr key={key}>
                                  {Array.from({ length: COLUMN_COUNT }, (_, index) => (
                                      <td key={String(index)}>
                                          <span className='mt-skel' style={{ width: '70%', height: 12 }} />
                                      </td>
                                  ))}
                              </tr>
                          ))
                        : rows.map(occurrence => {
                              const typeLabelId = getOccurrenceTypeLabelId(occurrence.occurrenceType);
                              return (
                                  <tr
                                      key={occurrence.id}
                                      className={`mt-row${selectedId === occurrence.id ? ' mt-row--selected' : ''}`}
                                      tabIndex={0}
                                      onClick={() => onOpen(occurrence)}
                                      onKeyDown={handleKeyDown(occurrence)}
                                  >
                                      <td className='mt-veh'>
                                          <b>{occurrence.vehicleLicensePlate || occurrence.vehicleChassis}</b>
                                          <span className='mt-mono'>{occurrence.vehicleChassis}</span>
                                          <small>{occurrence.vehicleModel || occurrence.model || ''}</small>
                                      </td>
                                      <td>
                                          <CriticalityBadge value={occurrence.criticality} fallback='–' />
                                      </td>
                                      <td className='mt-cell2'>
                                          {occurrence.customer || '–'}
                                          {typeLabelId && <small>{msg(typeLabelId)}</small>}
                                      </td>
                                      <td className='mt-cell2'>
                                          {occurrence.dealershipName || '–'}
                                          {occurrence.dealershipDn && (
                                              <small>
                                                  {[`DN ${occurrence.dealershipDn}`, occurrence.dealershipLocation]
                                                      .filter(Boolean)
                                                      .join(' · ')}
                                              </small>
                                          )}
                                      </td>
                                      <td className='mt-etapa'>{renderCurrentStep(occurrence)}</td>
                                      <td>{renderProgress(occurrence)}</td>
                                      <td className='mt-cell2 mt-num'>
                                          <b>
                                              {formatDuration(
                                                  getOccurrenceTotalMs(occurrence, timeSpentById.get(occurrence.id))
                                              )}
                                          </b>
                                          <small>
                                              {t('table.since', { date: formatDateTime(occurrence.createdAt) })}
                                          </small>
                                      </td>
                                      <td className='mt-num'>
                                          <small>
                                              {formatDateTime(
                                                  occurrence.lastWorklog?.updatedAt ?? occurrence.updatedAt
                                              )}
                                          </small>
                                      </td>
                                  </tr>
                              );
                          })}
                    {!loading && !rows.length && (
                        <tr>
                            <td colSpan={COLUMN_COUNT} className='mt-empty'>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default TrackingTable;
